import React from 'react';
import Icon from '../../../components/AppIcon';

const ChangeSummary = ({ modifications, booking, changeFees }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const hasChanges = () => {
    return (
      modifications?.dates ||
      modifications?.accommodation ||
      modifications?.activities?.added?.length > 0 ||
      modifications?.activities?.removed?.length > 0
    );
  };

  if (!hasChanges()) return null;

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg mb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Icon name="ClipboardList" size={24} />
          Change Summary
        </h2>
        <p className="text-sm text-white/80 mt-1">Review all modifications before confirming</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Date Changes */}
        {modifications?.dates && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Calendar" size={16} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Date Changes</h3>
            </div>
            <div className="ml-10 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm text-muted-foreground line-through">
                    {formatDate(booking?.checkIn)}
                  </span>
                  <Icon name="ArrowRight" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-success">
                    {formatDate(modifications?.dates?.checkIn)}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">Check-in</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm text-muted-foreground line-through">
                    {formatDate(booking?.checkOut)}
                  </span>
                  <Icon name="ArrowRight" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-success">
                    {formatDate(modifications?.dates?.checkOut)}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">Check-out</span>
              </div>
              <div className="p-3 bg-warning/10 rounded-lg border border-warning/20 mt-2">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} className="text-warning" />
                  <span className="text-sm text-warning font-medium">
                    Date modification fee: ₱500
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Accommodation Changes */}
        {modifications?.accommodation && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
                <Icon name="Building" size={16} className="text-secondary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Accommodation Change</h3>
            </div>
            <div className="ml-10 space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-sm text-muted-foreground line-through">
                    Current Room
                  </span>
                  <Icon name="ArrowRight" size={16} className="text-primary" />
                  <span className="text-sm font-semibold text-success">
                    {modifications?.accommodation?.label}
                  </span>
                </div>
              </div>
              <div className="p-3 bg-warning/10 rounded-lg border border-warning/20 mt-2">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} className="text-warning" />
                  <span className="text-sm text-warning font-medium">
                    Accommodation modification fee: ₱300
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Changes */}
        {(modifications?.activities?.added?.length > 0 || modifications?.activities?.removed?.length > 0) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Compass" size={16} className="text-accent" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Activity Changes</h3>
            </div>
            <div className="ml-10 space-y-2">
              {/* Added Activities */}
              {modifications?.activities?.added?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Added Activities:</p>
                  {modifications?.activities?.added?.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-success/10 rounded-lg border border-success/20"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="Plus" size={16} className="text-success" />
                        <span className="text-sm text-foreground">{activity?.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-success">
                        +₱{activity?.price?.toLocaleString('en-PH')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Removed Activities */}
              {modifications?.activities?.removed?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Removed Activities:</p>
                  {modifications?.activities?.removed?.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-error/10 rounded-lg border border-error/20"
                    >
                      <div className="flex items-center gap-2">
                        <Icon name="Minus" size={16} className="text-error" />
                        <span className="text-sm text-foreground line-through">{activity?.label}</span>
                      </div>
                      <span className="text-sm font-semibold text-error">
                        -₱{activity?.price?.toLocaleString('en-PH')}
                      </span>
                    </div>
                  ))}
                  <div className="p-3 bg-warning/10 rounded-lg border border-warning/20">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} className="text-warning" />
                      <span className="text-sm text-warning font-medium">
                        Cancellation fee: ₱{modifications?.activities?.removed?.length * 100} (₱100 per activity)
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Total Fees Summary */}
        {changeFees > 0 && (
          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between p-4 bg-warning/10 rounded-lg border border-warning/20">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} className="text-warning" />
                <span className="text-base font-semibold text-foreground">Total Modification Fees</span>
              </div>
              <span className="text-xl font-bold text-warning">
                ₱{changeFees?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className="p-4 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">Important Information</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• All modification fees are non-refundable</li>
                <li>• Changes are subject to availability confirmation</li>
                <li>• A new confirmation number will be issued upon successful modification</li>
                <li>• Original cancellation policy still applies to the modified booking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangeSummary;